import type { ReactNode } from "react";
import { useI18n } from "@wato/i18n/react";
import { Icon } from "../../rtl/glyphs";
import { IconButton } from "../IconButton/IconButton";
import { Popover } from "../Popover/Popover";

export interface FilterPopoverProps {
  /** Number of active filters (badge on the trigger). */
  readonly count: number;
  readonly children: ReactNode;
  readonly label?: string;
  readonly onClear?: () => void;
  readonly testId?: string;
}

/** «تصفية» icon button with an active-count badge; body = the filter form (sheet on phones). */
export function FilterPopover({ count, children, label, onClear, testId }: FilterPopoverProps) {
  const { t } = useI18n();
  const name = label ?? t("ui.filter.label");
  return (
    <Popover
      label={name}
      testId={testId}
      placement="start"
      panelClassName="w-72"
      trigger={(p) => (
        <IconButton {...p} label={name} icon={<Icon name="filter" />} badge={count} />
      )}
    >
      <div className="flex flex-col gap-3">
        {children}
        {onClear && count > 0 && (
          <button type="button" className="self-start text-sm text-fg underline" onClick={onClear}>
            {t("ui.filter.clearAll")}
          </button>
        )}
      </div>
    </Popover>
  );
}

export interface FilterChip {
  readonly id: string;
  readonly label: string;
}

export interface FilterChipsProps {
  readonly chips: readonly FilterChip[];
  readonly onRemove: (id: string) => void;
  readonly onClear?: () => void;
}

/** Removable chips under the toolbar; the «مسح الكل» link shows only with ≥ 1 chip. */
export function FilterChips({ chips, onRemove, onClear }: FilterChipsProps) {
  const { t } = useI18n();
  if (chips.length === 0) return null;
  return (
    <ul className="flex flex-wrap items-center gap-2" data-testid="filter-chips">
      {chips.map((c) => (
        <li
          key={c.id}
          className="inline-flex items-center gap-1 rounded-full border border-line bg-surface-sunken ps-3 pe-1 text-sm"
        >
          <span>{c.label}</span>
          <button
            type="button"
            aria-label={t("ui.filter.remove", { label: c.label })}
            title={t("ui.filter.remove", { label: c.label })}
            className="inline-flex size-6 items-center justify-center rounded-full hover:bg-surface pointer-coarse:size-8"
            onClick={() => onRemove(c.id)}
          >
            <Icon name="close" size={14} />
          </button>
        </li>
      ))}
      {onClear && (
        <li>
          <button type="button" className="text-sm text-fg underline" onClick={onClear}>
            {t("ui.filter.clearAll")}
          </button>
        </li>
      )}
    </ul>
  );
}
